import { useCallback, useEffect, useState } from "react";
import { Application, useApplication } from "@/contexts/ApplicationContext";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import { createSignedDocumentUrl } from "@/services/storageService";

export const useDocumentUploadState = () => {
  const { application, setApplication, isLoading } = useApplication();
  const { job } = useActiveJob();
  const [activeTab, setActiveTab] = useState("dvla-check");
  // Active tab is derived in deduceSubmittedTabs; avoid hardcoded defaults

  // DVLA states
  const [drivingLicenceNumber, setdrivingLicenceNumber] = useState("");
  const [dvlaAttempts, setDvlaAttempts] = useState(0); // Track number of failed DVLA verification attempts
  const [licenseFrontFile, setLicenseFrontFile] = useState<File | null>(null);
  const [licenseFrontPreview, setLicenseFrontPreview] = useState<string | null>(
    null
  );
  const [licenseBackFile, setLicenseBackFile] = useState<File | null>(null);
  const [licenseBackPreview, setLicenseBackPreview] = useState<string | null>(
    null
  );

  // RTW states
  const [rtwShareCode, setRtwShareCode] = useState("");
  const [rtwAttempts, setRtwAttempts] = useState(0); // Track number of failed RTW verification attempts
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);

  // Insurance states
  const [insuranceProvider, setInsuranceProvider] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [insuranceProofFile, setInsuranceProofFile] = useState<File | null>(
    null
  );
  const [insuranceProofPreview, setInsuranceProofPreview] = useState<
    string | null
  >(null);

  // CPC states (front/back + number)
  const [cpcFrontFile, setCpcFrontFile] = useState<File | null>(null);
  const [cpcFrontPreview, setCpcFrontPreview] = useState<string | null>(null);
  const [cpcBackFile, setCpcBackFile] = useState<File | null>(null);
  const [cpcBackPreview, setCpcBackPreview] = useState<string | null>(null);

  // Tacho Card states (front/back + number)
  const [tachoFrontFile, setTachoFrontFile] = useState<File | null>(null);
  const [tachoFrontPreview, setTachoFrontPreview] = useState<string | null>(
    null
  );
  const [tachoBackFile, setTachoBackFile] = useState<File | null>(null);
  const [tachoBackPreview, setTachoBackPreview] = useState<string | null>(null);
  const [tachoCardNumber, setTachoCardNumber] = useState<string>("");

  // D4 Medical states
  const [d4MedicalFile, setD4MedicalFile] = useState<File | null>(null);
  const [d4MedicalPreview, setD4MedicalPreview] = useState<string | null>(null);

  const deduceSubmittedTabs = useCallback(
    (application: Application) => {
      const submittedTabs: string[] = [];
      // Determine visible segments from job config in the exact backend order
      const documentsStage = job?.stages?.find((s) => s.id === "documents");
      let visibleTabsOrder: string[] =
        documentsStage?.segments?.map((seg) => seg.id) || [];
      // Fallback to known list filtered by presence if documents stage is missing
      if (!visibleTabsOrder.length) {
        const fallback = [
          "dvla-check",
          "rtw-check",
          "insurance-check",
          "cpc-upload",
          "tacho-card-upload",
          "d4-medical-report-upload",
        ];
        visibleTabsOrder = fallback.filter((id) =>
          job?.stages?.some((stage) =>
            stage.segments?.some((segment) => segment.id === id)
          )
        );
      }

      // Mark submitted based on existing documents
      if (
        application.documents.drivingLicenceNumber &&
        application.documents.licenseFront &&
        application.documents.licenseBack
      ) {
        submittedTabs.push("dvla-check");
      }
      if (
        application.documents.rtwShareCode ||
        application.documents.passportOrBirthCertificate
      ) {
        submittedTabs.push("rtw-check");
      }
      // Insurance requires all fields
      if (
        application.documents.insuranceProvider &&
        application.documents.policyNumber &&
        application.documents.insuranceProof
      ) {
        submittedTabs.push("insurance-check");
      }
      // CPC requires front and back + number
      if (application.documents.cpcFront && application.documents.cpcBack) {
        submittedTabs.push("cpc-upload");
      }
      // Tacho requires front and back + number
      if (
        application.documents.tachoFront &&
        application.documents.tachoBack &&
        application.documents.tachoCardNumber
      ) {
        submittedTabs.push("tacho-card-upload");
      }
      if (application.documents.d4MedicalReport) {
        submittedTabs.push("d4-medical-report-upload");
      }

      // Choose active tab
      // 1) Prefer current segment from application (backend-driven)
      const backendCurrentSegment =
        (application as unknown as Record<string, unknown>)[
          "current_segment"
        ] ||
        (application as unknown as Record<string, unknown>)["currentSegment"];
      let activeTab: string | undefined =
        typeof backendCurrentSegment === "string" &&
        visibleTabsOrder.includes(backendCurrentSegment)
          ? backendCurrentSegment
          : undefined;

      // 2) Otherwise, first visible tab not yet submitted
      if (!activeTab) {
        const nextIncomplete = visibleTabsOrder.find(
          (t) => !submittedTabs.includes(t)
        );
        if (nextIncomplete) {
          activeTab = nextIncomplete;
        }
      }

      // 3) Otherwise, last visible tab (or sensible default)
      if (!activeTab) {
        if (visibleTabsOrder.length) {
          activeTab = visibleTabsOrder[visibleTabsOrder.length - 1];
        } else {
          activeTab = job?.type === "Driver" ? "dvla-check" : "rtw-check";
        }
      }

      return {
        submittedTabs,
        activeTab,
      };
    },
    [job?.stages, job?.type]
  );

  // Track completion status
  const [submittedTabs, setSubmittedTabs] = useState<string[]>(() => {
    return deduceSubmittedTabs(application).submittedTabs;
  });

  useEffect(() => {
    console.log(application);
    setdrivingLicenceNumber(application.documents.drivingLicenceNumber || "");
    setRtwShareCode(application.documents.rtwShareCode || "");
    setInsuranceProvider(application.documents.insuranceProvider || "");
    setPolicyNumber(application.documents.policyNumber || "");
    setTachoCardNumber(application.documents.tachoCardNumber || "");

    const loadSignedUrls = async () => {
      const [
        licenseFront,
        licenseBack,
        rtw,
        insuranceProof,
        cpcFront,
        cpcBack,
        tachoFront,
        tachoBack,
        d4Medical,
      ] = await Promise.all([
        createSignedDocumentUrl(application.documents.licenseFront || ""),
        createSignedDocumentUrl(application.documents.licenseBack || ""),
        createSignedDocumentUrl(
          application.documents.passportOrBirthCertificate || ""
        ),
        createSignedDocumentUrl(application.documents.insuranceProof || ""),
        createSignedDocumentUrl(application.documents.cpcFront || ""),
        createSignedDocumentUrl(application.documents.cpcBack || ""),
        createSignedDocumentUrl(application.documents.tachoFront || ""),
        createSignedDocumentUrl(application.documents.tachoBack || ""),
        createSignedDocumentUrl(application.documents.d4MedicalReport || ""),
      ]);

      setLicenseFrontPreview(licenseFront);
      setLicenseBackPreview(licenseBack);
      setDocumentPreview(rtw);
      setInsuranceProofPreview(insuranceProof);
      setCpcFrontPreview(cpcFront);
      setCpcBackPreview(cpcBack);
      setTachoFrontPreview(tachoFront);
      setTachoBackPreview(tachoBack);
      setD4MedicalPreview(d4Medical);
    };

    loadSignedUrls();

    const { submittedTabs, activeTab } = deduceSubmittedTabs(application);
    console.log(submittedTabs, activeTab);
    setSubmittedTabs(submittedTabs);
    setActiveTab(activeTab);
  }, [application, deduceSubmittedTabs]);

  return {
    activeTab,
    setActiveTab,
    drivingLicenceNumber,
    setdrivingLicenceNumber,
    dvlaAttempts,
    setDvlaAttempts,
    rtwAttempts,
    setRtwAttempts,
    licenseFrontFile,
    setLicenseFrontFile,
    licenseFrontPreview,
    setLicenseFrontPreview,
    licenseBackFile,
    setLicenseBackFile,
    licenseBackPreview,
    setLicenseBackPreview,
    rtwShareCode,
    setRtwShareCode,
    documentFile,
    setDocumentFile,
    documentPreview,
    setDocumentPreview,
    insuranceProvider,
    setInsuranceProvider,
    policyNumber,
    setPolicyNumber,
    insuranceProofFile,
    setInsuranceProofFile,
    insuranceProofPreview,
    setInsuranceProofPreview,
    cpcFrontFile,
    setCpcFrontFile,
    cpcFrontPreview,
    setCpcFrontPreview,
    cpcBackFile,
    setCpcBackFile,
    cpcBackPreview,
    setCpcBackPreview,
    tachoFrontFile,
    setTachoFrontFile,
    tachoFrontPreview,
    setTachoFrontPreview,
    tachoBackFile,
    setTachoBackFile,
    tachoBackPreview,
    setTachoBackPreview,
    tachoCardNumber,
    setTachoCardNumber,
    d4MedicalFile,
    setD4MedicalFile,
    d4MedicalPreview,
    setD4MedicalPreview,
    submittedTabs,
    setSubmittedTabs,
    setApplication,
    isLoading,
  };
};
